import * as React from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PostPurchaseService from 'services/PostPurchaseService';
import { pacific, pct, usd } from './shared';
import { DesignStats, Taker, Upsell } from './types';

// The period of the stats on screen (the API's own from/to), not the one being picked.
type ShownPeriod = { from: string; to: string };

// Performance by design: one row per (upsell, design) over the page's period,
// sortable, filterable and paged. A row opens to the orders that took it.

type Row = DesignStats & { takeRate: number | null; revenuePerView: number | null; marginPerView: number | null };
type SortKey = keyof Row;

const PAGE_SIZES = [10, 25, 50];

const COLUMNS: { key: SortKey; label: string; hint?: string }[] = [
  { key: 'offered', label: 'Offered' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'takeRate', label: 'Take rate', hint: 'Accepted ÷ viewed.' },
  { key: 'declined', label: 'Declined' },
  { key: 'failed', label: 'Payment failed' },
  { key: 'added', label: 'Added', hint: 'Add-ons: accepted, and the products are confirmed on the Shopify order.' },
  { key: 'swapped', label: 'Swapped' },
  { key: 'swap_failed', label: 'Swap failed' },
  { key: 'errors', label: 'Errors' },
  { key: 'revenue', label: 'Revenue', hint: 'The extra charged, before tax.' },
  { key: 'margin', label: 'Est. margin', hint: 'Revenue less extra product cost, the card fee and freight (per added item for add-ons, the measured box freight for swaps); before refunds.' },
  { key: 'revenuePerView', label: 'Revenue / view' },
  { key: 'marginPerView', label: 'Margin / view' },
];

const OUTCOMES: Record<string, { label: string; color: 'success' | 'error' | 'default' }> = {
  swapped: { label: 'Swapped', color: 'success' },
  added: { label: 'Added', color: 'success' },
  swap_failed: { label: 'Swap failed', color: 'error' },
  accepted: { label: 'Accepted', color: 'default' },
};

const TAKERS_PAGE = 10;

// The orders that took one design, loaded when its row opens.
function Takers({ service, period, store, row }: { service: PostPurchaseService; period: ShownPeriod; store: string; row: Row }) {
  const [result, setResult] = React.useState<{ domain: string; takers: Taker[] } | null | undefined>(undefined);
  const [page, setPage] = React.useState(0);
  const { from, to } = period;
  React.useEffect(() => {
    let current = true;
    // A new period or store: drop the old list while the new one loads.
    setResult(undefined);
    setPage(0);
    service.getTakers(row.upsellId as string, row.designId as string, { from, to }, store).then(response => current && setResult(response));
    return () => {
      current = false;
    };
  }, [service, from, to, store, row.upsellId, row.designId]);
  if (result === undefined) return <LinearProgress aria-label="loading orders that took it" sx={{ my: 1 }} />;
  if (result === null) return <Alert severity="error" sx={{ my: 1 }}>Could not load the orders that took it.</Alert>;
  if (!result.takers.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No order took it in this period.</Typography>;
  const shop = result.domain.replace('.myshopify.com', '');
  return (
    // Pinned to the left of the wide table, so it stays in view however far it scrolls.
    <Box sx={{ py: 1, position: 'sticky', left: 0, width: 'min(640px, 100%)' }}>
      <Table size="small" aria-label="orders that took it">
        <TableHead>
          <TableRow>
            <TableCell>Order</TableCell>
            <TableCell>Accepted (Pacific)</TableCell>
            <TableCell align="right">Charged</TableCell>
            <TableCell>Outcome</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.takers.slice(page * TAKERS_PAGE, (page + 1) * TAKERS_PAGE).map(taker => (
            <TableRow key={taker.checkoutToken}>
              <TableCell>
                {taker.orderId ? (
                  <a href={`https://admin.shopify.com/store/${shop}/orders/${taker.orderId}`} target="_blank" rel="noreferrer">
                    {taker.orderName ?? taker.orderId}
                  </a>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>{pacific(taker.at)}</TableCell>
              <TableCell align="right">{taker.charge == null ? '—' : usd(taker.charge)}</TableCell>
              <TableCell>
                <Chip size="small" label={OUTCOMES[taker.outcome]?.label ?? taker.outcome} color={OUTCOMES[taker.outcome]?.color ?? 'default'} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {result.takers.length > TAKERS_PAGE && (
        <TablePagination
          component="div"
          count={result.takers.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={TAKERS_PAGE}
          rowsPerPageOptions={[TAKERS_PAGE]}
        />
      )}
    </Box>
  );
}

function PerformanceRow({
  row,
  current,
  addOn,
  service,
  period,
  store,
}: {
  row: Row;
  current: boolean;
  addOn: boolean;
  service: PostPurchaseService;
  period: ShownPeriod;
  store: string;
}) {
  const [open, setOpen] = React.useState(false);
  // Error rows recorded before the request named an offer have no design to open.
  const openable = row.upsellId != null && row.designId != null && row.accepted > 0;
  const money = (value: number | null) => (value == null ? '—' : usd(value));
  return (
    <>
      <TableRow
        hover={openable}
        onClick={openable ? () => setOpen(was => !was) : undefined}
        sx={{ cursor: openable ? 'pointer' : 'default', '& > td': { borderBottom: open ? 0 : undefined } }}
      >
        <TableCell padding="checkbox">
          {openable && (
            <IconButton size="small" aria-label="show orders" aria-expanded={open}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.upsellId ?? '—'}</TableCell>
        <TableCell>
          <Typography variant="body2" fontFamily="monospace" component="span">{row.designId ?? '—'}</Typography>
          {current && <Chip size="small" label="current" sx={{ ml: 1 }} />}
        </TableCell>
        <TableCell align="right">{row.offered}</TableCell>
        <TableCell align="right">{row.viewed}</TableCell>
        <TableCell align="right">{row.accepted}</TableCell>
        <TableCell align="right">{pct(row.accepted, row.viewed)}</TableCell>
        <TableCell align="right">{row.declined}</TableCell>
        <TableCell align="right">{row.failed}</TableCell>
        {/* Fewer added than accepted means a customer paid and the products
            aren't on the order (or aren't confirmed yet): worth a look. */}
        <TableCell align="right" sx={addOn && row.added < row.accepted ? { color: 'warning.main', fontWeight: 700 } : undefined}>
          {addOn ? row.added : '—'}
        </TableCell>
        <TableCell align="right">{addOn ? '—' : row.swapped}</TableCell>
        {/* An unresolved failed swap means a customer paid and their order
            still has the replaced items: always worth a look. One a later
            attempt completed is shown as recovered, not as a problem. */}
        {addOn ? (
          <TableCell align="right">—</TableCell>
        ) : (
          <TableCell align="right" sx={row.swap_failed ? { color: 'error.main', fontWeight: 700 } : undefined}>
            {row.swap_failed}
            {row.swap_recovered ? (
              <Tooltip title="Failed at first, then swapped (by a retry or by hand). Nothing to do.">
                <Typography component="div" variant="caption" sx={{ color: 'success.main', whiteSpace: 'nowrap' }}>
                  {row.swap_recovered} recovered
                </Typography>
              </Tooltip>
            ) : null}
          </TableCell>
        )}
        <TableCell align="right" sx={row.errors ? { color: 'warning.main' } : undefined}>{row.errors}</TableCell>
        <TableCell align="right">{usd(row.revenue)}</TableCell>
        <TableCell align="right">{money(row.margin)}</TableCell>
        <TableCell align="right">{money(row.revenuePerView)}</TableCell>
        <TableCell align="right">{money(row.marginPerView)}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{pacific(row.firstSeen)} – {pacific(row.lastSeen)}</TableCell>
      </TableRow>
      {openable && (
        <TableRow>
          <TableCell colSpan={COLUMNS.length + 4} sx={{ py: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Takers service={service} period={period} store={store} row={row} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function PerformanceTable({
  stats,
  upsells,
  service,
  period,
  store,
}: {
  stats: DesignStats[];
  upsells: Upsell[];
  service: PostPurchaseService;
  period: ShownPeriod;
  store: string;
}) {
  const [upsellFilter, setUpsellFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [currentOnly, setCurrentOnly] = React.useState(false);
  const [sort, setSort] = React.useState<{ key: SortKey; desc: boolean }>({ key: 'revenue', desc: true });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(PAGE_SIZES[0]);

  const currentDesign = new Map(upsells.map(upsell => [upsell.id, upsell.designId]));
  const isAddOn = (upsellId: string | null) => upsells.find(upsell => upsell.id === upsellId)?.kind === 'add_on';
  const isCurrent = (row: DesignStats) => row.upsellId != null && currentDesign.get(row.upsellId) === row.designId;

  const rows: Row[] = stats.map(row => ({
    ...row,
    takeRate: row.viewed ? row.accepted / row.viewed : null,
    revenuePerView: row.viewed ? row.revenue / row.viewed : null,
    marginPerView: row.viewed && row.margin != null ? row.margin / row.viewed : null,
  }));
  const needle = search.trim().toLowerCase();
  const filtered = rows.filter(
    row =>
      (upsellFilter === 'all' || row.upsellId === upsellFilter) &&
      (!currentOnly || isCurrent(row)) &&
      (!needle || `${row.upsellId ?? ''} ${row.designId ?? ''}`.toLowerCase().includes(needle)),
  );
  // Unknowns (null) sort last either way.
  const sorted = [...filtered].sort((a, b) => {
    const [x, y] = [a[sort.key], b[sort.key]];
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return sort.desc ? -order : order;
  });
  const lastPage = Math.max(0, Math.ceil(sorted.length / pageSize) - 1);
  const shownPage = Math.min(page, lastPage);

  const sortBy = (key: SortKey) => {
    setSort(current => ({ key, desc: current.key === key ? !current.desc : true }));
    setPage(0);
  };
  const header = (key: SortKey, label: string, align: 'left' | 'right', hint?: string) => (
    <TableCell key={key} align={align} sortDirection={sort.key === key ? (sort.desc ? 'desc' : 'asc') : false} sx={{ whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key && !sort.desc ? 'asc' : 'desc'} onClick={() => sortBy(key)}>
        {hint ? (
          <Tooltip title={hint} describeChild>
            <span>{label}</span>
          </Tooltip>
        ) : (
          label
        )}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', px: 2, py: 1.5 }}>
        <Select size="small" value={upsellFilter} onChange={event => { setUpsellFilter(String(event.target.value)); setPage(0); }} inputProps={{ 'aria-label': 'upsell' }}>
          <MenuItem value="all">All upsells</MenuItem>
          {upsells.map(upsell => (
            <MenuItem key={upsell.id} value={upsell.id}>{upsell.id}</MenuItem>
          ))}
        </Select>
        <TextField size="small" placeholder="Search upsell or design" value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} inputProps={{ 'aria-label': 'search designs' }} />
        <FormControlLabel
          control={<Switch size="small" checked={currentOnly} onChange={event => { setCurrentOnly(event.target.checked); setPage(0); }} />}
          label={<Typography variant="body2">Current designs only</Typography>}
        />
      </Box>
      {!sorted.length ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {stats.length ? 'No design matches these filters.' : 'No upsell activity in this period.'}
        </Typography>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="upsell performance">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {header('upsellId', 'Upsell', 'left')}
                  {header('designId', 'Design', 'left')}
                  {COLUMNS.map(column => header(column.key, column.label, 'right', column.hint))}
                  {header('lastSeen', 'Seen (Pacific)', 'left')}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.slice(shownPage * pageSize, (shownPage + 1) * pageSize).map(row => (
                  <PerformanceRow
                    key={`${row.upsellId}:${row.designId}`}
                    row={row}
                    current={isCurrent(row)}
                    addOn={isAddOn(row.upsellId)}
                    service={service}
                    period={period}
                    store={store}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={sorted.length}
            page={shownPage}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setPage(0); }}
          />
        </>
      )}
    </>
  );
}
